<script lang="ts">
    import vm, { JourneyLine, LineType } from '@journeys/Journeys.viewmodel';
    import PresetColors from '@shared/components/ColorPickers/PresetColors';
    import LineMenu from './LineMenu.vue';
    import { computed, reactive } from 'vue';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Jorney_Line',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const props = defineProps<{
            line: JourneyLine;
            movePoint: Func;
            removePoint: Func;
            removeLine: Func;
            addPoint: Func;
            colors: any;
        }>(),
        { editor, viewport } = vm,
        state = reactive({
            sel: {
                end: editor.tools.sel_points[`l${props.line.id}to`] ? true : false,
            },
        });

    const points = computed(() => props.line.points.reduce((acc: string, { x, y }: { x: number; y: number }) => acc + ` ${x} ${y} `, ''));

    //** way of adding new properties
    props.line.t ||= editor.state.activeLineStyle.t;
    props.line.c ||= editor.state.activeLineStyle.c;

    const color = computed<any>(() => {
        return props.line.c;
    });

    const type = computed<any>(() => {
        if (props.line.to === null || props.line.fr === null)
            return {
                dasharray: 6,
                linecap: 'butt',
                strokeWidth: 2,
            };

        switch (props.line.t) {
            case LineType.Dotted:
                return {
                    dasharray: '0,10',
                    linecap: 'round',
                    strokeWidth: 3.5,
                };
            case LineType.Dashed:
                return {
                    dasharray: 6,
                    linecap: 'butt',
                    strokeWidth: 2,
                };
            // case LineType.Solid
            default:
                return {
                    dasharray: 0,
                    linecap: 'butt',
                    strokeWidth: 2,
                };
        }
    });

    const openLineSettings = (ev: MouseEvent) => {
        if (viewport.contextMenuTargets[props.line.id]) return;
        else for (let id in viewport.contextMenuTargets) editor.events.emit('closecontextmenu', id);

        let x = ev.x,
            y = ev.y + 30;

        viewport.contextMenuTargets[props.line.id] = props.line.id;

        LineMenu._props = {
            data: props.line,
            colors: props.colors,
        };
        LineMenu._class = 'minimal';

        editor.events.emit('opencontextmenu', LineMenu, { x, y }, viewport.dims);
    };
</script>

<template>
    <g class="line-group">
        <path
            class="line"
            :d="`M ${line.runtime.frVertex ?? `${line.x} ${line.y}`} L ${points} ${line.runtime.toVertex ?? `${line.ex} ${line.ey}`}`"
            :stroke="PresetColors[color].hsl"
            :stroke-dasharray="type.dasharray"
            :stroke-linecap="type.linecap"
            :stroke-width="type.strokeWidth"
            fill="none"
            :marker-end="`url(#arrow${color})`"
            v-bind="!line.runtime.el ? ({ ref: (el: Element) => (line.runtime.el = el as SVGPathElement) } as any) : null" />

        <!-- line controls -->
        <!-- hitboxes -->
        <path
            v-for="({ x, y, ex, ey }, hidx) in line.runtime.hitboxes"
            :key="`hitbox_${line.id}`"
            class="hitbox"
            :d="`M ${x} ${y} L ${ex} ${ey}`"
            @pointerdown.left="(ev: any) => addPoint(ev, line, hidx)" />
        <circle
            class="point hitbox"
            :class="editor.tools.sel_points[`l${line.id}fr`] && 'selected'"
            @pointerdown.left="(ev: any) => movePoint(ev, line, line, 'x', 'y', 'fr', 'fr')"
            :cx="line.x"
            :cy="line.y"
            r="4"
            fill="#1f1c26" />
        <circle
            class="point hitbox"
            @pointerdown.left="(ev: any) => movePoint(ev, line, line, 'ex', 'ey', 'to', 'to')"
            :cx="line.ex"
            :cy="line.ey"
            r="4"
            fill="none" />
        <g v-for="({ x, y }, pidx) in line.points" :key="`l${line.id}p${pidx}`" class="point-wrapper">
            <circle
                class="point hitbox"
                :class="editor.tools.sel_points[`l${line.id}p${pidx}`] && 'selected'"
                @pointerdown.left="(ev: any) => movePoint(ev, line, line.points[pidx], 'x', 'y', null, pidx)"
                :cx="x"
                :cy="y"
                r="4"
                fill="#1f1c26" />
            <use
                href="#removebtn"
                class="point-btns"
                :x="x + 12"
                :y="y - 12"
                width="15"
                height="15"
                @click.left.stop="() => removePoint(line, pidx, `l${line.id}p${pidx}`)" />
        </g>

        <!-- TODO: //* move getPointAtLength & getTotalLength to a better place -->
        <use
            href="#removebtn"
            class="line-btns"
            v-bind="line.runtime.el?.getPointAtLength(23)"
            width="20"
            height="20"
            @click.left.stop="() => removeLine(line)" />
        <use
            href="#settingsbtn"
            class="line-btns"
            :class="line.runtime.el?.getTotalLength()! < 70 ? 'concealed' : ''"
            v-bind="line.runtime.el?.getPointAtLength(50)"
            width="20"
            height="20"
            @click.left.stop="openLineSettings" />
    </g>
</template>

<style>
    .line-btns.concealed {
        pointer-events: none;
    }
</style>
